import * as React from 'react';
import * as styles from './styles.scss';
import type { AssetCommonViewProps } from 'owa-timestream-asset-sdk-types';
import AssetError from './AssetError';
import type { AssetId } from 'owa-timestream-types';
import { AssetIdProvider } from '../hooks/useAssetId';
import { DueDatePicker } from 'owa-timestream-asset-sdk-duedates';
import { ErrorBoundary } from 'owa-error-boundary';
import { observer } from 'mobx-react-lite';
import AssetOptions from './AssetOptions';
import InAppMessagingCallout from './InAppMessagingCallout';
import useAssetViewBaseViewState from '../util/useAssetViewBaseViewState';
import ResizeableElement from './ResizeableElement';

export default observer(function AssetViewBase({
    assetId,
    View,
    assetAttributes,
}: {
    assetId: AssetId;
    View: React.ComponentType<AssetCommonViewProps>;
    assetAttributes?: React.HTMLAttributes<HTMLDivElement>;
}) {
    const ref = React.useRef<HTMLDivElement>(null);
    const {
        baseAttributes,
        wrapperAttributes,
        viewProps,
        assetErrorProps,
        assetOptionsProps,
        shouldShowDueDatePicker,
        shouldAssetRenderDueDate,
        shouldAssetRenderOptions,
        dueDatePickerProps,
    } = useAssetViewBaseViewState(assetId, ref, assetAttributes);

    const dueDate = shouldShowDueDatePicker ? (
        <DueDatePicker
            className={styles.dueDateRoot}
            iconClassName={styles.dueDateIcon}
            chooseDateText={''}
            hideDateOnCompletion={true}
            {...dueDatePickerProps}
        />
    ) : null;

    const options = <AssetOptions assetRef={ref} {...assetOptionsProps} />;

    return (
        <AssetIdProvider value={assetId}>
            <ResizeableElement attributes={wrapperAttributes} assetId={assetId} ref={ref}>
                <div {...baseAttributes}>
                    <ErrorBoundary
                        fullErrorComponent={AssetError}
                        fullErrorComponentProps={assetErrorProps}>
                        {!shouldAssetRenderDueDate && dueDate}
                        <View
                            {...viewProps}
                            DueDate={shouldAssetRenderDueDate && dueDate}
                            Options={shouldAssetRenderOptions && options}
                        />
                        {!shouldAssetRenderOptions && options}
                    </ErrorBoundary>
                </div>
            </ResizeableElement>
            {viewProps.showCallOut && viewProps.lightningId && viewProps.inAppMessage && (
                <InAppMessagingCallout
                    target={ref}
                    lightningId={viewProps.lightningId}
                    inAppMessage={viewProps.inAppMessage}
                />
            )}
        </AssetIdProvider>
    );
});
